import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { themr } from 'react-css-themr';
import isFunction from 'lodash/isFunction';
import classnames from 'classnames';
import { dashboardColors } from 'common/enums/ColorEnum';

import theme from './LocationMarker.scss';
import { DraggableCore } from 'react-draggable';
import LocationMarker from './LocationMarker';
import _ from 'lodash';
import { Portal } from 'react-portal';
import LatLngUtils from 'common/utils/LatLngUtils';

const MARKER_WIDTH = 20;
const MARKER_HEIGHT = 24;

const defaults = {
    theme,
    spiderMarkerStyle: {
        position: 'relative',
        height: MARKER_HEIGHT,
        width: MARKER_WIDTH
    }
};

const spiderLocationMarkerFactory = ({ Marker, options = {} }) => {
    const {
        theme: defaultTheme,
        spiderMarkerStyle: defaultSpiderMarkerStyle,
        disabledSpiderMarkerStyle
    } = {
        ...defaults,
        ...options
    };

    class SpiderLocationMarkerComponent extends Component {
        constructor(props) {
            super(props);
            this._isMounted = true;

            // If using portal, then initializing the portal marker's default position
            // as per the passed latLng { lat, lng }
            if (props.usePortal) {
                const pixel = LatLngUtils.getLatLngToPixel(
                    props.bboxMap,
                    props.map,
                    { lat: props.lat, lng: props.lng }
                );
                this.state = {
                    x: pixel.x,
                    y: pixel.y
                };
            }
            this.state = {
                ...this.state,
                dragging: !!props.usePortal
            };
        }

        componentDidMount() {
            if (this.props.isSelected) {
                this.focus();
            }
        }

        UNSAFE_componentWillReceiveProps(nextProps) {
            if (
                nextProps.isSelected &&
                this.props.isSelected !== nextProps.isSelected
            ) {
                this.focus();
            }
        }

        componentWillUnmount() {
            this._isMounted = false;
        }

        focus = () => {
            var element = ReactDOM.findDOMNode(this);
            element.scrollIntoView({
                block: 'end',
                behavior: 'smooth'
            });
        };

        onDrag = event => {
            let clientX;
            let clientY;
            const {
                disableSpiderMarkerDrag,
                isEditable,
                handleDragStart
            } = this.props;
            if (isEditable && !disableSpiderMarkerDrag) {
                if (event.type === 'touchmove') {
                    [{ clientX, clientY }] = event.touches;
                } else {
                    ({ clientX, clientY } = event);
                }
                if (this.isCursorInBounds({ clientX, clientY })) {
                    this.setState(
                        {
                            dragging: true,
                            x: clientX,
                            y: clientY
                        },
                        handleDragStart &&
                            handleDragStart(this.props, this.state)
                    );
                }
            }
        };

        updatePosition({ lat, lng }) {
            const { bboxMap, map } = this.props;
            const pixel = LatLngUtils.getLatLngToPixel(bboxMap, map, {
                lat,
                lng
            });
            this.setState({ x: pixel.x, y: pixel.y });
        }

        isCursorInBounds = event => {
            const { bboxMap } = this.props;
            var { left, top, right, bottom } = isFunction(bboxMap)
                ? bboxMap()
                : bboxMap;
            var { clientX, clientY } = event;
            if (clientY < top || clientY > bottom) return false;
            if (clientX < left || clientY > right) return false;
            return true;
        };

        onStop = () => {
            const { disableSpiderMarkerDrag, locationEntity } = this.props;
            if (!disableSpiderMarkerDrag) {
                if (this.state.dragging) {
                    this.props.handleDragDone(this.props, this.state);
                } else {
                    this.props.handleLocationSelect({ locationEntity });
                }
                if (this._isMounted)
                    this.setState({
                        dragging: false
                    });
            }
        };

        onMouseEnter = e => {
            const { onMouseEnter, id } = this.props;
            onMouseEnter(id, e);
        };

        onPopUpCloseClick = id => {
            const { onPopUpCloseClick } = this.props;
            onPopUpCloseClick && onPopUpCloseClick(id);
            this.onMouseLeave();
        };

        onMouseLeave = e => {
            const { onMouseLeave, id } = this.props;
            onMouseLeave(id, e);
        };

        renderMarker = () => {
            const { usePortal } = this.props;
            const { dragging, x, y } = this.state;

            var draggableMarkerSpanStyle = {};
            if (dragging) {
                draggableMarkerSpanStyle = {
                    position: 'fixed',
                    left: x - defaultSpiderMarkerStyle.width / 2,
                    top: y - defaultSpiderMarkerStyle.height + 1
                };

                if (usePortal) {
                    draggableMarkerSpanStyle = {
                        ...draggableMarkerSpanStyle,
                        zIndex: 40000 // a large enough value so that it is always at the top
                    };
                }
            }
            const {
                theme,
                isVisible,
                isDisabled,
                isDeadMarker = false
            } = this.props;
            return isVisible ? (
                <DraggableCore
                    onStart={_.noop}
                    onDrag={this.onDrag}
                    onStop={this.onStop}
                >
                    <span
                        style={draggableMarkerSpanStyle}
                        className={classnames({
                            [theme.draggableMarkerWrap]: !usePortal
                        })}
                    >
                        {isDeadMarker ? (
                            <div
                                style={{
                                    position: 'relative',
                                    width: 15,
                                    height: 15,
                                    background: dashboardColors.salmonTwo.code,
                                    borderRadius: 8,
                                    border: `2px solid ${dashboardColors.white.code}`
                                }}
                                {...this.props}
                            />
                        ) : Marker ? (
                            <Marker
                                {...this.props}
                                className={classnames({
                                    [theme.markerHover]: usePortal
                                })}
                                withArrow
                                overrideStyle={
                                    isDisabled
                                        ? disabledSpiderMarkerStyle
                                        : defaultSpiderMarkerStyle
                                }
                                onMouseEnter={this.onMouseEnter}
                                onMouseLeave={this.onMouseLeave}
                                onPopUpCloseClick={this.onPopUpCloseClick}
                            />
                        ) : (
                            <LocationMarker
                                {...this.props}
                                withArrow
                                overrideStyle={
                                    isDisabled
                                        ? disabledSpiderMarkerStyle
                                        : defaultSpiderMarkerStyle
                                }
                                onMouseEnter={this.onMouseEnter}
                                onMouseLeave={this.onMouseLeave}
                            />
                        )}
                    </span>
                </DraggableCore>
            ) : null;
        };

        render() {
            const { usePortal } = this.props;
            return usePortal ? (
                <Portal>{this.renderMarker()}</Portal>
            ) : (
                this.renderMarker()
            );
        }
    }

    return themr('SpiderLocationMarker', defaultTheme, {
        withRef: true
    })(SpiderLocationMarkerComponent);
};

const SpiderLocationMarker = spiderLocationMarkerFactory({});

export default SpiderLocationMarker;
export { SpiderLocationMarker, spiderLocationMarkerFactory };
